// src/components/store/sections/hero-banner/HeroCategorySidebar.tsx
"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import Link from "next/link";
import { 
  Menu,
  ChevronRight,
  Smartphone,
  Laptop,
  Home as HomeIcon,
  Shirt,
  Sparkles,
  Baby,
  Trophy,
  BookOpen,
  ShoppingBasket,
  HeartPulse,
  Car,
  MoreHorizontal,
  ArrowRight,
  Zap,
  Truck,
  ShieldCheck,
  RotateCcw,
  Headphones,
  Layers
} from "lucide-react";

export interface HeroBannerItem {
  id: number;
  badge_title?: string;
  title: string;
  subtitle?: string;
  button_text?: string;
  target_url?: string;
  image?: string | null;
  font_family?: string;
  title_color?: string;
  subtitle_color?: string;
  bg_color?: string;
  gradient_color_1?: string;
  gradient_color_2?: string;
  gradient_color_3?: string;
  gradient_direction?: string;
}

export interface CategoryItem {
  id: number | string;
  name: string;
  slug: string;
  image?: string | null;
  icon?: string;
  product_count?: number;
}

function getCategoryIcon(name: string) {
  const lower = name.toLowerCase();
  if (lower.includes("veg") || lower.includes("groc") || lower.includes("food") || lower.includes("harvest")) return ShoppingBasket;
  if (lower.includes("mobile") || lower.includes("phone")) return Smartphone;
  if (lower.includes("elect") || lower.includes("laptop")) return Laptop;
  if (lower.includes("home") || lower.includes("living") || lower.includes("decor")) return HomeIcon;
  if (lower.includes("fashion") || lower.includes("cloth") || lower.includes("wear")) return Shirt;
  if (lower.includes("beauty") || lower.includes("cosmetic")) return Sparkles;
  if (lower.includes("baby") || lower.includes("toy")) return Baby;
  if (lower.includes("sport")) return Trophy;
  if (lower.includes("book")) return BookOpen;
  if (lower.includes("health")) return HeartPulse;
  if (lower.includes("auto")) return Car;
  return Layers;
}

export default function HeroCategorySidebar({
  banners = [],
  categories = [],
  primaryColor = "#059669",
  subdomain = "greenharvest",
}: {
  banners?: HeroBannerItem[];
  categories?: CategoryItem[];
  primaryColor?: string;
  subdomain?: string;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeCategories, setActiveCategories] = useState<CategoryItem[]>([]);

  // 🌟 ১. ব্যাকএন্ড ক্যাটাগরি লোড করা (Fallback সহ)
  useEffect(() => {
    if (categories && categories.length > 0) {
      setActiveCategories(categories);
    } else {
      // যদি প্রপস হিসেবে ক্যাটাগরি না আসে তবে ব্যাকএন্ড এপিআই থেকে সরাসরি ফেচ করবে
      async function fetchBackendCategories() {
        try {
          const isLocal =
            typeof window !== "undefined" &&
            (window.location.hostname.includes("localhost") ||
              window.location.hostname === "127.0.0.1");

          const apiUrl = isLocal
            ? `http://${subdomain}.localhost:8000/api/v1/products/categories/`
            : `/api/v1/products/categories/`;

          const res = await fetch(apiUrl, { headers: { Accept: "application/json" } });
          if (res.ok) {
            const data = await res.json();
            const list = Array.isArray(data) ? data : data?.results || [];
            setActiveCategories(list);
          }
        } catch (err) {
          console.error("Failed to fetch sidebar categories:", err);
        }
      }
      fetchBackendCategories();
    }
  }, [categories, subdomain]);

  // 🌟 ২. অটো-স্লাইডার টাইমার
  const nextSlide = useCallback(() => {
    if (banners.length > 1) {
      setCurrentIndex((prev) => (prev === banners.length - 1 ? 0 : prev + 1));
    }
  }, [banners.length]);

  useEffect(() => {
    if (!banners || banners.length <= 1) return;
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(interval);
  }, [banners, nextSlide]);

  const currentBanner = banners[currentIndex] || banners[0];

  const formatImageUrl = (img?: string | null) => {
    if (!img) return null;
    if (img.startsWith("http://") || img.startsWith("https://")) return img;
    return `http://127.0.0.1:8000${img.startsWith("/") ? "" : "/"}${img}`;
  };

  const bannerImageUrl = formatImageUrl(currentBanner?.image);

  return (
    <section className="max-w-7xl mx-auto px-3 sm:px-6 pt-4">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-stretch">
        
        {/* 🌟 ১. বাম পাশের ক্যাটাগরি সাইডবার (ভার্টিক্যাল স্ক্রোল সহ) */}
        <aside className="hidden lg:flex lg:col-span-3 flex-col bg-white rounded-3xl border border-slate-200/90 shadow-sm overflow-hidden h-full max-h-[500px]">
          {/* ক্যাটাগরি হেডার */}
          <div className="px-5 py-3.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between text-slate-900 font-black text-sm shrink-0">
            <div className="flex items-center gap-2.5">
              <Menu className="w-4 h-4 text-slate-700" />
              <span>All Categories</span>
            </div>
            <Link 
              href="/categories" 
              className="text-[11px] font-bold text-slate-400 hover:text-slate-700 transition-colors flex items-center gap-0.5"
            >
              <span>View All</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {/* 🌟 ভার্টিক্যাল স্ক্রোলেবল ক্যাটাগরি লিস্ট */}
          <div className="flex-1 overflow-y-auto p-2 space-y-1 [scrollbar-width:thin] [scrollbar-color:#e2e8f0_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-slate-200 [&::-webkit-scrollbar-thumb]:rounded-full">
            {activeCategories.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-400">
                ক্যাটাগরি লোড হচ্ছে...
              </div>
            ) : (
              activeCategories.map((item) => {
                const IconComp = getCategoryIcon(item.name);
                const categoryImg = formatImageUrl(item.image);

                return (
                  <Link
                    key={item.id}
                    href={`/categories/${item.slug || ""}`}
                    className="group flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-all text-xs font-semibold text-slate-700 hover:text-slate-950"
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      {/* ক্যাটাগরি ইমেজ অথবা আইকন */}
                      {categoryImg ? (
                        <div className="w-6 h-6 rounded-lg overflow-hidden relative shrink-0 border border-slate-100 bg-slate-50">
                          <Image
                            src={categoryImg}
                            alt={item.name}
                            fill
                            unoptimized
                            className="object-cover group-hover:scale-110 transition-transform"
                          />
                        </div>
                      ) : (
                        <div 
                          style={{ color: primaryColor }}
                          className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0 bg-slate-100/70 group-hover:bg-white transition-colors"
                        >
                          <IconComp className="w-3.5 h-3.5 transition-transform group-hover:scale-110" />
                        </div>
                      )}

                      <span className="truncate">{item.name}</span>
                    </div>

                    <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-600 group-hover:translate-x-0.5 transition-all shrink-0" />
                  </Link>
                );
              })
            )}
          </div>
        </aside>

        {/* 🌟 ২. ডান পাশের ব্যানার ও ট্রাস্ট ব্যাজ কলাম */}
        <div className="lg:col-span-9 flex flex-col justify-between gap-4">
          
          {/* ব্যানার স্লাইডার ফ্রেম */}
          <div className="relative rounded-3xl overflow-hidden min-h-[380px] sm:min-h-[440px] flex items-center p-6 sm:p-12 shadow-sm border border-sky-100 bg-gradient-to-br from-[#dff2fe] via-[#edf7ff] to-[#e4f4ff]">
            
            {/* ব্যাকগ্রাউন্ড ডেকোরেটিভ টেক্সট */}
            <div className="absolute top-6 right-8 sm:right-16 text-right pointer-events-none select-none opacity-85 hidden sm:block">
              <span className="text-xl sm:text-2xl font-serif italic text-slate-700 block leading-tight">
                Better Products
              </span>
              <span className="text-2xl sm:text-3xl font-serif italic font-bold text-slate-900 block leading-tight">
                Better Life
              </span>
              <svg className="w-20 h-3 ml-auto mt-1" viewBox="0 0 100 12" fill="none">
                <path d="M2 10C30 2 70 2 98 10" stroke="#0284c7" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>

            {/* স্লাইড কনটেন্ট */}
            <div 
              key={currentIndex}
              className="w-full grid grid-cols-1 md:grid-cols-12 gap-6 items-center relative z-10 animate-in fade-in duration-500"
            >
              {/* টেক্সট কলাম */}
              <div className="md:col-span-6 space-y-4 sm:space-y-5 text-left">
                {/* Discount Badge */}
                <span 
                  style={{ backgroundColor: primaryColor }}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-white text-[11px] font-black uppercase tracking-wider shadow-sm"
                >
                  <Zap className="w-3.5 h-3.5 fill-current" />
                  {currentBanner?.badge_title || "FLAT 20% DISCOUNT"}
                </span>

                {/* মেইন টাইটেল */}
                <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-slate-900 leading-[1.12]">
                  {currentBanner?.title || "Purely Fresh Organic Harvest"}
                </h1>

                {/* সাবটাইটেল */}
                <p className="text-xs sm:text-sm text-slate-600 font-medium max-w-sm leading-relaxed">
                  {currentBanner?.subtitle || "100% natural, farm-fresh products delivered with care to your doorstep."}
                </p>

                {/* শপ নাও বাটন */}
                <div className="pt-2">
                  <Link
                    href={currentBanner?.target_url || "/products"}
                    style={{ backgroundColor: primaryColor }}
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-xl text-white text-xs font-black shadow-md hover:opacity-95 active:scale-95 transition-all cursor-pointer"
                  >
                    <span>{currentBanner?.button_text || "Shop Now"}</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* প্রোডাক্ট ইমেজ কলাম */}
              <div className="md:col-span-6 flex items-center justify-center relative min-h-[220px] sm:min-h-[280px]">
                {bannerImageUrl ? (
                  <div className="relative w-64 h-64 sm:w-80 sm:h-80 drop-shadow-2xl">
                    <Image
                      src={bannerImageUrl}
                      alt={currentBanner?.title || "Banner Product"}
                      fill
                      unoptimized
                      priority
                      className="object-contain"
                    />
                  </div>
                ) : (
                  <div className="relative w-full h-64 flex items-center justify-center">
                    <div className="w-56 h-12 bg-sky-200/60 rounded-full blur-md absolute bottom-4" />
                    <img
                      src="https://images.unsplash.com/photo-1540420773420-3366772f4999?w=600&fit=crop&q=80"
                      alt="Organic Showcase"
                      className="w-56 sm:w-64 h-auto object-contain drop-shadow-2xl relative z-10 rounded-2xl"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* স্লাইড ডট ইন্ডিকেটর */}
            {banners.length > 1 && (
              <div className="absolute bottom-5 left-6 sm:left-12 flex items-center gap-1.5 z-20">
                {banners.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setCurrentIndex(idx)}
                    style={idx === currentIndex ? { backgroundColor: primaryColor } : undefined}
                    className={`h-2 rounded-full transition-all cursor-pointer ${
                      idx === currentIndex ? "w-6" : "w-2 bg-slate-300 hover:bg-slate-400"
                    }`}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* 🌟 ৩. ট্রাস্ট ব্যাজ সেকশন */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-3.5 sm:p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-800 shrink-0">
                <Truck className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h4 className="text-xs font-black text-slate-900 truncate">Cash on Delivery</h4>
                <p className="text-[10px] text-slate-500 truncate">Pay when you receive</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-800 shrink-0">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h4 className="text-xs font-black text-slate-900 truncate">Secure Payment</h4>
                <p className="text-[10px] text-slate-500 truncate">100% secure transactions</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-800 shrink-0">
                <RotateCcw className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h4 className="text-xs font-black text-slate-900 truncate">Easy Return</h4>
                <p className="text-[10px] text-slate-500 truncate">Hassle free return policy</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-800 shrink-0">
                <Headphones className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h4 className="text-xs font-black text-slate-900 truncate">24/7 Support</h4>
                <p className="text-[10px] text-slate-500 truncate">We're here to help</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}